import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concat,
  concatMap,
  filter,
  map,
  of,
  withLatestFrom,
} from 'rxjs';
import { InitFacade } from 'src/app/store/facade/init.facade';
import * as actions from '../actions/expenses.actions';
import { ExpenseService } from '../expense.service';
import { ExpensesFacade } from '../facade/expenses.facade';
import { Expenses, SelectedUserExpenses } from '../state/expenses.state';

@Injectable({
  providedIn: 'root',
})
export class ExpensesEffects {
  constructor(
    private actions$: Actions,
    private intiFacade: InitFacade,
    private facade: ExpensesFacade,
    private service: ExpenseService,
  ) {}

  getExpenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getExpenses),
      withLatestFrom(this.intiFacade.userId$),
      concatMap(([action, userId]) => {
        return this.service.getExpenses(userId).pipe(
          map((expenses: Expenses[]) => {
            return actions.getExpensesSuccess({ expenses });
          }),
          catchError(() => of(actions.getExpensesError())),
        );
      }),
    ),
  );

  createNewExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createNewExpense),
      withLatestFrom(this.intiFacade.userId$),
      concatMap(([action, userId]) => {
        return this.service.createNewExpense(userId, action.newExpense).pipe(
          concatMap(() =>
            concat(
              of(actions.createNewExpenseSuccess()),
              of(actions.getExpenses()),
            ),
          ),
          catchError(() => of(actions.createNewExpenseError())),
        );
      }),
    ),
  );

  getExpenseTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getExpenseTransactions),
      withLatestFrom(this.intiFacade.userId$, this.facade.selectedExpense$),
      filter(([_, userId, selectedExpense]) => !!selectedExpense && !!userId),
      concatMap(([_, userId, selectedExpense]) =>
        this.service
          .getExpenseTransactions(
            userId,
            selectedExpense.userId,
            selectedExpense.paymentStatus,
          )
          .pipe(
            map((selectedUserExpenses: SelectedUserExpenses[]) => {
              return actions.getExpenseTransactionsSuccess({
                selectedUserExpenses,
              });
            }),
            catchError(() => of(actions.getExpenseTransactionsError())),
          ),
      ),
    ),
  );

  updateExpenseTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateExpenseTransaction),
      withLatestFrom(this.intiFacade.userId$, this.facade.selectedUserExpense$),
      concatMap(([action, userId, selectedUserExpense]) => {
        return this.service
          .updateExpenseTransaction(
            userId,
            selectedUserExpense.expenseId,
            action.payload,
          )
          .pipe(
            concatMap(() =>
              concat(
                of(actions.updateExpenseTransactionSuccess()),
                of(actions.getExpenses()),
                of(actions.getExpenseTransactions()),
              ),
            ),
            catchError(() => of(actions.updateExpenseTransactionError())),
          );
      }),
    ),
  );

  deleteExpenseTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteExpenseTransaction),
      withLatestFrom(this.intiFacade.userId$),
      concatMap(([action, userId]) => {
        return this.service
          .deleteExpenseTransaction(userId, action.expenseId)
          .pipe(
            concatMap(() =>
              concat(
                of(
                  actions.deleteExpenseTransactionSuccess({
                    userId: userId,
                    expenseId: action.expenseId,
                  }),
                ),
                of(actions.getExpenses()),
                of(actions.getExpenseTransactions()),
              ),
            ),
            catchError(() => of(actions.deleteExpenseTransactionError())),
          );
      }),
    ),
  );
}
